import { useCallback, useEffect, useRef, useState } from 'react'
import {
	useForm,
	type DefaultValues,
	type FieldValues,
	type Resolver,
	type UseFormProps,
	type UseFormReturn,
} from 'react-hook-form'
import { useFormDraft } from './wizard-drafts-store'

const PERSIST_DEBOUNCE_MS = 300

interface UsePersistedFormOptions<T extends FieldValues> {
	/** Stable key under which the draft is stored in localStorage. */
	key:           string
	defaultValues: DefaultValues<T>
	resolver?:     Resolver<T>
	mode?:         UseFormProps<T>['mode']
	/**
	 * Gate for persistence and hydration. Pass dialog `open` state, or `true`
	 * for always-on forms (e.g. inline pages). Hydration happens when this
	 * transitions from false → true; persistence is active while it's true.
	 */
	active:          boolean
	/** Optional initial step (defaults to 0). Persisted and re-hydrated. */
	initialStep?:    number
	/**
	 * When false (default: true), the form always opens at step 0 with
	 * defaultValues — draft is still saved but never auto-restored on open.
	 */
	restoreOnOpen?:  boolean
	/**
	 * Версия редактируемой записи — обычно её `updatedAt`.
	 *
	 * Черновик, записанный до того, как запись изменили, отбрасывается.
	 * Иначе он ложится поверх свежих значений: поле открывается пустым там,
	 * где значение есть, а сохранение записывает эту пустоту обратно.
	 * Не указана — черновик восстанавливается как прежде.
	 */
	recordVersion?:  string
}

export interface UsePersistedFormResult<T extends FieldValues> {
	form:       UseFormReturn<T>
	step:       number
	setStep:    (s: number) => void
	/**
	 * Clears the draft from storage. Call on successful submit so the next
	 * open starts fresh.
	 */
	resetDraft: () => void
}

/**
 * Wraps `react-hook-form`'s `useForm` with automatic draft persistence to
 * localStorage (via `useFormDraft`). Use the same way as `useForm`, plus
 * a `key` and an `active` gate. On submit success, call `resetDraft()`.
 *
 * Key invariant: NEVER call `form.reset(...)` from your close handlers — the
 * hook re-hydrates on every `active=true` transition, which is the single
 * source of truth for form values. Resetting from elsewhere triggers the
 * `form.watch` subscription and overwrites the just-saved draft.
 */
export function usePersistedForm<T extends FieldValues>(
	options: UsePersistedFormOptions<T>,
): UsePersistedFormResult<T> {
	const { key, defaultValues, resolver, mode, active, initialStep = 0, restoreOnOpen = true, recordVersion } = options

	const draft = useFormDraft<T>(key)
	// Merged over defaultValues rather than replacing them: a draft persisted
	// before a schema change carries no value for fields added since, and
	// substituting it wholesale would leave those `undefined` — which reads as
	// "required field left empty" and blocks submit on a form the user never
	// touched.
	const merge = (d: { state: T; stepIdx: number } | undefined): T =>
		(d ? { ...(defaultValues as object), ...(d.state as object) } : defaultValues) as T

	const seed  = restoreOnOpen ? draft.read(recordVersion) : undefined

	const [step, setStep] = useState<number>(seed?.stepIdx ?? initialStep)

	const form = useForm<T>({
		resolver,
		defaultValues: merge(seed) as UseFormProps<T>['defaultValues'],
		mode,
	})

	// Hydrate from draft on each active=true transition. This is the only
	// place form.reset is called — keeps it as the single source of truth.
	useEffect(() => {
		if (!active) return
		const d = restoreOnOpen ? draft.read(recordVersion) : undefined
		form.reset(merge(d))
		setStep(d?.stepIdx ?? initialStep)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active])

	// Persist values + step on every change while active. Debounced so each
	// keystroke / checkbox click doesn't trigger a synchronous JSON.stringify
	// of the whole form state (which causes visible UI jitter on large forms).
	// When true, all draft writes are suppressed (set by resetDraft, cleared on next open).
	const suppressedRef = useRef(false)

	// Re-enable writes each time the form becomes active (dialog opens).
	useEffect(() => {
		if (active) suppressedRef.current = false
	}, [active])

	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	useEffect(() => {
		if (!active) return
		const sub = form.watch((values) => {
			if (suppressedRef.current) return
			if (debounceRef.current) clearTimeout(debounceRef.current)
			debounceRef.current = setTimeout(() => {
				if (!suppressedRef.current) draft.write(values as T, step, recordVersion)
			}, PERSIST_DEBOUNCE_MS)
		})
		return () => {
			sub.unsubscribe()
			if (debounceRef.current) clearTimeout(debounceRef.current)
		}
	}, [active, step, form, draft])

	// Step changes are rare and intentional — persist immediately.
	useEffect(() => {
		if (!active || suppressedRef.current) return
		draft.write(form.getValues(), step, recordVersion)
	}, [step, active, form, draft])

	// Flush pending debounce on unmount or when active flips off, so we don't
	// lose the last edit if the dialog closes within the debounce window.
	useEffect(() => {
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current)
				if (active && !suppressedRef.current) draft.write(form.getValues(), step, recordVersion)
			}
		}
	}, [active, draft, form, step])

	// Cancels pending debounce, clears the draft, and suppresses all subsequent
	// writes until the form is reopened. Call on successful submit.
	const resetDraft = useCallback(() => {
		suppressedRef.current = true
		if (debounceRef.current) {
			clearTimeout(debounceRef.current)
			debounceRef.current = null
		}
		draft.reset()
	}, [draft])

	return { form, step, setStep, resetDraft }
}
